import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CubeIcon, MapPinIcon, CalendarIcon, ExclamationCircleIcon, TruckIcon, ArrowPathIcon, EyeIcon, XMarkIcon, UserIcon, CheckCircleIcon, ExclamationTriangleIcon, PhoneIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { deviseCommande } from '../utils/devise';

/**
 * Page publique pour que les livreurs voient leurs commandes en cours
 * Sans authentification - accès via URL avec token UUID unique et sécurisé
 */
const DeliveryPersonPublicView = () => {
  // Récupérer le token d'accès sécurisé depuis l'URL
  const { token } = useParams();

  // États pour les données et le chargement
  const [deliveryData, setDeliveryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // État pour afficher/masquer les commandes livrées - Masqué par défaut
  const [showDelivered, setShowDelivered] = useState(false);

  /**
   * Construit les bornes locales des sept derniers jours pour éviter un décalage
   * de date lié à la conversion UTC. La période comprend J-6 et aujourd'hui J.
  */
  const [dateRange] = useState(() => {
    /**
     * Transforme une date du navigateur en clé YYYY-MM-DD dans son fuseau local.
     * Cette clé est utilisée par l'API afin que le début et la fin de période restent exacts.
     */
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const today = new Date();
    const sixDaysAgo = new Date(today);
    sixDaysAgo.setDate(today.getDate() - 6);

    return {
      startDate: formatLocalDate(sixDaysAgo),
      endDate: formatLocalDate(today)
    };
  });
  const { startDate, endDate } = dateRange;

  // État des accordéons : toutes les journées sont fermées à la première visite.
  const [openDateKeys, setOpenDateKeys] = useState({});

  // État pour le modal de détails
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // États pour les actions du livreur
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [problemNote, setProblemNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(null);

  /**
   * Fonction pour récupérer les commandes du livreur via le token sécurisé
   */
  const fetchOrders = async (accessToken, start, end) => {
    if (!accessToken) return;

    setLoading(true);
    setError(null);

    try {
      // Appel à l'API publique sans authentification avec token sécurisé
      // On passe maintenant les dates de début et de fin à l'API
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/delivery-persons/public/${accessToken}/orders`,
        {
          params: {
            includeDelivered: true,
            startDate: start,
            endDate: end
          }
        }
      );

      setDeliveryData(response.data);
    } catch (err) {
      console.error('Erreur lors de la récupération des commandes:', err);

      // Gestion des différents types d'erreurs
      if (err.response?.status === 404) {
        setError('Lien invalide ou livreur inactif');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Impossible de charger les commandes. Vérifiez votre connexion ou réessayez plus tard.');
      }
      setDeliveryData(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Charger les données au montage du composant ou quand les filtres de date changent
   */
  useEffect(() => {
    if (token) {
      fetchOrders(token, startDate, endDate);
    }
  }, [token, startDate, endDate]);

  /**
   * Ouvrir le modal avec les détails de la commande
   */
  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  /**
   * Fermer le modal de détails
   */
  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedOrder(null);
    setActionSuccess(null);
  };

  /**
   * Marquer une commande comme livrée
   * Met à jour l'état local sans recharger toutes les commandes
   */
  const handleMarkAsDelivered = async () => {
    if (!selectedOrder || !token) return;

    if (!window.confirm('Confirmer que cette commande a été livrée ?')) {
      return;
    }

    setActionLoading(true);
    setActionSuccess(null);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/delivery-persons/public/orders/${selectedOrder.id}/update`,
        {
          token: token,
          action: 'delivered'
        }
      );

      setActionSuccess('Commande marquée comme livrée !');

      // Mettre à jour l'état local au lieu de recharger
      const updatedOrder = response.data.order;

      // Mettre à jour la commande dans la liste
      setDeliveryData(prevData => ({
        ...prevData,
        orders: prevData.orders.map(order =>
          order.id === selectedOrder.id ? { ...order, delivery_notes: updatedOrder.delivery_notes } : order
        )
      }));

      // Mettre à jour la commande sélectionnée pour le modal
      setSelectedOrder(prev => ({ ...prev, delivery_notes: updatedOrder.delivery_notes }));

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        handleCloseModal();
      }, 2000);
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      alert(err.response?.data?.error || 'Erreur lors de la mise à jour de la commande');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Ouvrir le modal pour signaler un problème
   */
  const handleOpenProblemModal = () => {
    setProblemNote('');
    setShowProblemModal(true);
  };

  /**
   * Signaler un problème
   * Met à jour l'état local sans recharger toutes les commandes
   */
  const handleReportProblem = async () => {
    if (!selectedOrder || !token) return;

    if (!problemNote.trim()) {
      alert('Veuillez décrire le problème');
      return;
    }

    setActionLoading(true);
    setActionSuccess(null);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/delivery-persons/public/orders/${selectedOrder.id}/update`,
        {
          token: token,
          action: 'problem',
          note: problemNote
        }
      );

      setActionSuccess('Problème signalé avec succès !');
      setShowProblemModal(false);
      setProblemNote(''); // Réinitialiser le champ

      // Mettre à jour l'état local au lieu de recharger
      const updatedOrder = response.data.order;

      // Mettre à jour la commande dans la liste
      setDeliveryData(prevData => ({
        ...prevData,
        orders: prevData.orders.map(order =>
          order.id === selectedOrder.id ? { ...order, delivery_notes: updatedOrder.delivery_notes } : order
        )
      }));

      // Mettre à jour la commande sélectionnée pour le modal
      setSelectedOrder(prev => ({ ...prev, delivery_notes: updatedOrder.delivery_notes }));

      // Fermer complètement après 2 secondes pour laisser voir le message
      setTimeout(() => {
        setActionSuccess(null);
      }, 2000);
    } catch (err) {
      console.error('Erreur lors du signalement:', err);
      alert(err.response?.data?.error || 'Erreur lors du signalement du problème');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Fonction pour obtenir la couleur du statut
   */
  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800',
      preparing: 'bg-yellow-100 text-yellow-800',
      ready_to_deliver: 'bg-purple-100 text-purple-800',
      in_delivery: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      completed: 'bg-green-200 text-green-900', // Commande livrée/terminée
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  /**
   * Fonction pour obtenir le libellé du statut
   */
  const getStatusLabel = (status) => {
    const labels = {
      new: 'Nouvelle',
      confirmed: 'Confirmée',
      preparing: 'En préparation',
      ready_to_deliver: 'Prête pour livraison',
      in_delivery: 'En livraison',
      delivered: 'Livrée',
      completed: 'Livrée', // Statut completed = commande livrée
      cancelled: 'Annulée',
    };
    return labels[status] || status;
  };

  /**
   * Fonction pour tronquer le texte à un nombre de mots maximum
   */
  const truncateWords = (text, maxWords) => {
    if (!text) return '';
    const words = text.split(' ');
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  /**
   * Vérifie si le livreur a déjà marqué cette commande comme livrée
   * en cherchant dans les delivery_notes
   */
  const isMarkedAsDelivered = (order) => {
    if (!order.delivery_notes) return false;
    return order.delivery_notes.includes('Commande livrée par');
  };

  /**
   * Vérifie si un problème a été signalé pour cette commande
   * en cherchant dans les delivery_notes
   */
  const isProblemReported = (order) => {
    if (!order.delivery_notes) return false;
    return order.delivery_notes.includes('Problème signalé par');
  };

  /**
   * Vérifie si une commande est livrée (statut completed)
   */
  const isOrderDelivered = (order) => {
    return order.status === 'completed';
  };

  /**
   * Formate une date pour l'affichage en-tête de groupe
   * @param {string} dateStr - Date au format ISO
   * @returns {string} Date formatée en français
   */
  const formatDateHeader = (dateStr) => {
    if (!dateStr) return 'Date non définie';
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Vérifier si c'est aujourd'hui ou demain
    if (date.toDateString() === today.toDateString()) {
      return "Aujourd'hui";
    }
    if (date.toDateString() === tomorrow.toDateString()) {
      return "Demain";
    }

    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  /**
   * Construit les sept journées de la période, puis y range les commandes par date de livraison.
   * Les journées sans commande sont conservées afin que le livreur voie toute sa semaine.
   * @param {Array} orders - Liste des commandes
   * @returns {Object} Commandes groupées par date { dateKey: { label: string, orders: [] } }
   */
  const groupOrdersByDate = (orders) => {
    const grouped = {};
    const currentDate = new Date(`${endDate}T12:00:00`);
    const firstDate = new Date(`${startDate}T12:00:00`);

    // Créer toutes les journées de la période, même si aucune commande n'y est affectée.
    while (currentDate >= firstDate) {
      const dateKey = currentDate.toISOString().split('T')[0];
      grouped[dateKey] = {
        label: formatDateHeader(dateKey),
        orders: []
      };
      currentDate.setDate(currentDate.getDate() - 1);
    }

    (orders || []).forEach((order) => {
      // La date est déjà fournie au format ISO par l'API ; on conserve sa partie jour.
      const dateKey = order.delivery_date?.slice(0, 10);

      if (dateKey && grouped[dateKey]) {
        grouped[dateKey].orders.push(order);
      }
    });

    return grouped;
  };

  /**
   * Ouvre ou ferme une liste journalière sans modifier les autres listes.
   * Chaque livreur peut ainsi consulter plusieurs jours de sa semaine en parallèle.
   */
  const handleToggleDateGroup = (dateKey) => {
    setOpenDateKeys((previousOpenDateKeys) => ({
      ...previousOpenDateKeys,
      [dateKey]: !previousOpenDateKeys[dateKey]
    }));
  };

  // Affichage pendant le chargement
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <ArrowPathIcon className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Chargement des commandes...</p>
        </div>
      </div>
    );
  }

  // Affichage en cas d'erreur
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <ExclamationCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Affichage si pas de token dans l'URL
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <TruckIcon className="h-16 w-16 text-blue-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Lien requis</h2>
          <p className="text-gray-600">
            Veuillez accéder à cette page via le lien personnel qui vous a été fourni.
          </p>
        </div>
      </div>
    );
  }

  // Affichage principal avec le tableau des commandes
  const filteredOrders = deliveryData?.orders?.filter(order => {
    // Le filtrage par date est maintenant fait côté serveur
    // On garde uniquement le filtre pour afficher/masquer les livrées localement
    if (!showDelivered && order.status === 'completed') return false;
    return true;
  }) || [];
  const dayGroups = groupOrdersByDate(filteredOrders);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* En-tête - Adapté pour mobile (flex-col) et desktop (flex-row) */}
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-center text-center sm:text-left gap-4">
              <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <TruckIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {deliveryData?.delivery_person?.name || 'Livreur'}
                </h1>
                <p className="text-gray-600">{deliveryData?.delivery_person?.phone}</p>
              </div>
            </div>

            {/* Filtre et Statistiques */}
            <div className="flex flex-col sm:items-end gap-4 w-full sm:w-auto">
              <div className="flex flex-wrap items-center gap-3 w-full justify-center sm:justify-end">
                {/* Période fixe des commandes affichées */}
                <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                  <CalendarIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-xs font-bold text-gray-700">7 derniers jours</span>
                </div>

                {/* Bouton pour afficher/masquer les livrées */}
                <button
                  onClick={() => setShowDelivered(!showDelivered)}
                  className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-bold text-xs transition shadow-sm ${
                    showDelivered 
                      ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                  }`}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center transition ${
                    showDelivered ? 'bg-green-600 border-green-600' : 'bg-white border-gray-400'
                  }`}>
                    {showDelivered && <CheckCircleIcon className="h-3 w-3 text-white" />}
                  </div>
                  {showDelivered ? 'Livrées affichées' : 'Livrées masquées'}
                </button>
              </div>

              <div className="flex gap-4 w-full sm:w-auto">
                {/* Commandes en cours (non livrées) */}
                <div className="text-center bg-blue-50 p-3 rounded-lg flex-1 sm:flex-none min-w-[80px]">
                  <p className="text-[10px] text-blue-600 uppercase tracking-wider font-bold">En cours</p>
                  <p className="text-2xl font-black text-blue-600">
                    {deliveryData?.orders?.filter(o => o.status !== 'completed').length || 0}
                  </p>
                </div>
                {/* Commandes livrées (statut completed) */}
                <div className="text-center bg-green-50 p-3 rounded-lg flex-1 sm:flex-none min-w-[80px]">
                  <p className="text-[10px] text-green-600 uppercase tracking-wider font-bold">Livrées</p>
                  <p className="text-2xl font-black text-green-600">
                    {deliveryData?.orders?.filter(o => o.status === 'completed').length || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Listes déroulantes des sept jours, du plus récent au plus ancien */}
        {Object.keys(dayGroups).length > 0 ? (
          <div className="space-y-6">
            {/* Vue Mobile : une liste déroulante accessible par journée */}
            <div className="space-y-2 md:hidden">
            {Object.entries(dayGroups).map(([dateKey, group]) => (
              <div key={dateKey} className="space-y-2">
                {/* Bouton d'accordéon : l'état ouvert est annoncé aux lecteurs d'écran. */}
                <button
                  type="button"
                  onClick={() => handleToggleDateGroup(dateKey)}
                  aria-expanded={Boolean(openDateKeys[dateKey])}
                  aria-controls={`delivery-day-${dateKey}`}
                  className="w-full min-h-11 flex items-center gap-3 px-3 py-2 text-left bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition"
                >
                  <CalendarIcon className="h-5 w-5 text-blue-600 flex-shrink-0" aria-hidden="true" />
                  <span className="flex-1 text-base font-bold text-gray-800">{group.label}</span>
                  <span className="text-sm text-gray-500">{group.orders.length} commande{group.orders.length > 1 ? 's' : ''}</span>
                  <ChevronDownIcon className={`h-5 w-5 text-blue-600 flex-shrink-0 transition-transform ${openDateKeys[dateKey] ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>

                {/* Vue Mobile : Affichage des commandes du groupe */}
                {openDateKeys[dateKey] && (
                <div id={`delivery-day-${dateKey}`} className="grid grid-cols-1 gap-2">
                  {group.orders.length === 0 && (
                    <p className="px-3 py-4 text-sm text-center text-gray-500 bg-white border border-gray-200 rounded-lg">Aucune commande prévue pour cette journée.</p>
                  )}
                  {group.orders.map((order) => (
                    <div
                      key={order.id}
                      className={`rounded-lg shadow-sm border overflow-hidden transition ${
                        isOrderDelivered(order)
                          ? 'bg-gray-100 border-gray-200 opacity-70'
                          : 'bg-white border-gray-100'
                      }`}
                    >
                      <div className="p-2.5 space-y-2">
                        {/* Ligne 1 : Numéro de téléphone (pour appeler), Lieu et Total */}
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {/* Badge statut livré visible si la commande est livrée */}
                            {isOrderDelivered(order) && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-600 text-white text-[10px] font-bold rounded flex-shrink-0">
                                <CheckCircleIcon className="h-3 w-3" />
                                Livrée
                              </span>
                            )}

                            {/* Numéro pour appeler direct */}
                            {order.customer?.phone ? (
                              <a
                                href={`tel:${order.customer.phone}`}
                                className={`flex items-center gap-1 text-xs font-black px-2 py-1 rounded-md flex-shrink-0 ${
                                  isOrderDelivered(order)
                                    ? 'text-gray-500 bg-gray-200'
                                    : 'text-blue-600 bg-blue-50'
                                }`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <PhoneIcon className="h-3 w-3" />
                                {order.customer.phone}
                              </a>
                            ) : (
                              <span className="text-xs font-black text-gray-400 flex-shrink-0">Pas de n°</span>
                            )}

                            {/* Lieu de livraison */}
                            <div className="flex items-center gap-1 text-gray-500 min-w-0">
                              <MapPinIcon className="h-3 w-3 flex-shrink-0 text-gray-400" />
                              <p className="text-xs truncate">{order.delivery_address}</p>
                            </div>
                          </div>

                          {/* Montant Total */}
                          <div className="text-right flex-shrink-0">
                            <span className={`text-sm font-black whitespace-nowrap ${
                              isOrderDelivered(order) ? 'text-gray-500' : 'text-blue-600'
                            }`}>
                              {Number(order.total_amount).toLocaleString('fr-FR')} <span className="text-[10px]">CFA</span>
                            </span>
                          </div>
                        </div>

                        {/* Ligne 2 : Notes de la commande et Bouton Détails */}
                        <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-gray-100">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {/* Numéro de commande (petit rappel) */}
                            <span className="text-[10px] font-bold text-gray-400 flex-shrink-0">#{order.order_number}</span>

                            {/* Badge si marqué comme livré (par le livreur) */}
                            {isMarkedAsDelivered(order) && !isOrderDelivered(order) && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded flex-shrink-0">
                                <CheckCircleIcon className="h-3 w-3" />
                                Marqué livré
                              </span>
                            )}

                            {/* Badge si problème signalé */}
                            {isProblemReported(order) && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded flex-shrink-0">
                                <ExclamationTriangleIcon className="h-3 w-3" />
                                Problème signalé
                              </span>
                            )}

                            {/* Notes (uniquement si présentes) */}
                            <div className="text-[11px] truncate flex-1 italic text-amber-600 font-medium">
                              {order.notes && `Note: ${order.notes}`}
                            </div>
                          </div>

                          {/* Bouton pour voir les détails complets (articles, etc) */}
                          <button
                            onClick={() => handleViewDetails(order)}
                            className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-bold rounded-md transition ${
                              isOrderDelivered(order)
                                ? 'bg-gray-200 hover:bg-gray-300 text-gray-600'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                          >
                            <EyeIcon className="h-3 w-3" />
                            Détails
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            ))}
            </div>

            {/* Vue Desktop : même accordéon, avec un tableau quand la journée est ouverte */}
            <div className="hidden md:block space-y-6">
              {Object.entries(dayGroups).map(([dateKey, group]) => (
                <div key={dateKey} className="bg-white rounded-xl shadow-md overflow-hidden">
                  {/* En-tête de groupe Desktop transformé en bouton déroulant. */}
                  <button
                    type="button"
                    onClick={() => handleToggleDateGroup(dateKey)}
                    aria-expanded={Boolean(openDateKeys[dateKey])}
                    aria-controls={`delivery-day-desktop-${dateKey}`}
                    className="w-full min-h-12 bg-blue-50 border-b border-blue-100 px-6 py-3 flex items-center gap-3 text-left hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition"
                  >
                    <CalendarIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
                    <span className="flex-1 text-lg font-bold text-blue-900">{group.label}</span>
                    <span className="text-sm text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                      {group.orders.length} commande{group.orders.length > 1 ? 's' : ''}
                    </span>
                    <ChevronDownIcon className={`h-5 w-5 text-blue-600 transition-transform ${openDateKeys[dateKey] ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>

                  {openDateKeys[dateKey] && (
                  <div id={`delivery-day-desktop-${dateKey}`} className="overflow-x-auto">
                    {group.orders.length === 0 ? (
                      <p className="px-6 py-6 text-sm text-center text-gray-500">Aucune commande prévue pour cette journée.</p>
                    ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            N° Commande
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Téléphone
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Lieu de livraison
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Notes
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Prix total
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {group.orders.map((order) => (
                          <tr
                            key={order.id}
                            className={`transition ${
                              isOrderDelivered(order)
                                ? 'bg-gray-100 opacity-70'
                                : 'bg-white hover:bg-gray-50'
                            }`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className={`text-sm font-medium ${isOrderDelivered(order) ? 'text-gray-500' : 'text-gray-900'}`}>
                                #{order.order_number}
                              </div>
                              <div className="text-xs mt-1">
                                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                                  {getStatusLabel(order.status)}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {order.customer?.phone ? (
                                <a
                                  href={`tel:${order.customer.phone}`}
                                  className={`inline-flex items-center gap-2 text-sm font-bold hover:underline ${
                                    isOrderDelivered(order) ? 'text-gray-500' : 'text-blue-600'
                                  }`}
                                >
                                  <PhoneIcon className="h-4 w-4" />
                                  {order.customer.phone}
                                </a>
                              ) : (
                                <span className="text-sm text-gray-400 italic">Non renseigné</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className={`text-sm max-w-xs truncate ${isOrderDelivered(order) ? 'text-gray-500' : 'text-gray-900'}`}>
                                <MapPinIcon className="h-4 w-4 inline mr-1 text-gray-400" />
                                {order.delivery_address}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm max-w-xs space-y-1">
                                {order.notes ? (
                                  <span className={`px-2 py-1 rounded text-xs block ${
                                    isOrderDelivered(order) ? 'text-gray-600 bg-gray-200' : 'text-blue-700 bg-blue-50'
                                  }`}>
                                    {order.notes}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 italic">-</span>
                                )}
                                {/* Badge si marqué comme livré (par le livreur mais pas encore statut delivered) */}
                                {isMarkedAsDelivered(order) && !isOrderDelivered(order) && (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                                    <CheckCircleIcon className="h-3.5 w-3.5" />
                                    Marqué livré
                                  </span>
                                )}
                                {/* Badge si problème signalé */}
                                {isProblemReported(order) && (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded">
                                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                                    Problème signalé
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className={`text-sm font-bold ${isOrderDelivered(order) ? 'text-gray-500' : 'text-gray-900'}`}>
                                {Number(order.total_amount).toLocaleString('fr-FR')} {deviseCommande(order)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => handleViewDetails(order)}
                                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition ${
                                  isOrderDelivered(order)
                                    ? 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                                }`}
                              >
                                <EyeIcon className="h-4 w-4" />
                                Détails
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    )}
                  </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <CubeIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucune commande trouvée
            </h3>
            <p className="text-gray-600">
              {deliveryData?.orders?.length > 0 
                ? "Aucune commande ne correspond aux filtres actuels (dates ou statut)."
                : "Vous n'avez pas de commandes à livrer pour le moment."}
            </p>
          </div>
        )}
      </div>

      {/* Modal de détails de commande - Optimisé pour mobile */}
      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full sm:max-w-3xl max-h-[95vh] flex flex-col overflow-hidden">
            {/* En-tête du modal - Fixe en haut */}
            <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 truncate mr-2">
                Commande #{selectedOrder.order_number}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition flex-shrink-0"
              >
                <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
              </button>
            </div>

            {/* Contenu du modal - Scrollable */}
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
              {/* Statut */}
              <div className="flex justify-center sm:justify-start">
                <span className={`inline-flex px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(selectedOrder.status)} shadow-sm`}>
                  {getStatusLabel(selectedOrder.status)}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* Informations client */}
                {selectedOrder.customer && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100 shadow-sm">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <UserIcon className="h-5 w-5 text-blue-600" />
                      Informations client
                    </h3>
                    <div className="space-y-2">
                      <p className="text-gray-700 text-sm sm:text-base">
                        <span className="font-semibold text-gray-500 text-xs uppercase block sm:inline sm:mr-1">Nom:</span> {selectedOrder.customer.name}
                      </p>
                      {selectedOrder.customer.phone && (
                        <p className="text-gray-700 text-sm sm:text-base">
                          <span className="font-semibold text-gray-500 text-xs uppercase block sm:inline sm:mr-1">Téléphone:</span>{' '}
                          <a href={`tel:${selectedOrder.customer.phone}`} className="text-blue-600 hover:underline font-bold inline-flex items-center gap-1">
                            {selectedOrder.customer.phone}
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Détails de livraison */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 shadow-sm">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <TruckIcon className="h-5 w-5 text-blue-600" />
                    Livraison
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">Adresse</p>
                        <p className="text-gray-700 text-sm">{selectedOrder.delivery_address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">Date prévue</p>
                        <p className="text-gray-700 text-sm">
                          {selectedOrder.delivery_date
                            ? new Date(selectedOrder.delivery_date).toLocaleDateString('fr-FR', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })
                            : 'Non définie'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes et rapports */}
              {(selectedOrder.notes || selectedOrder.delivery_notes) && (
                <div className="space-y-3">
                  {selectedOrder.notes && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg shadow-sm">
                      <p className="text-xs font-bold text-amber-900 uppercase mb-1">
                        Notes de la commande
                      </p>
                      <p className="text-sm text-amber-800 whitespace-pre-wrap">{selectedOrder.notes}</p>
                    </div>
                  )}

                  {selectedOrder.delivery_notes && (
                    <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg shadow-sm">
                      <p className="text-xs font-bold text-indigo-900 uppercase mb-1">
                        Rapports du livreur
                      </p>
                      <p className="text-sm text-indigo-800 whitespace-pre-wrap">{selectedOrder.delivery_notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Articles de la commande */}
              <div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <CubeIcon className="h-5 w-5 text-blue-600" />
                  Articles ({selectedOrder.order_items?.length || 0})
                </h3>
                <div className="space-y-2 border border-gray-100 rounded-lg divide-y divide-gray-100 bg-white">
                  {selectedOrder.order_items?.map((item) => (
                    <div key={item.id} className="flex gap-3 p-3">
                      {/* Image du produit */}
                      <div className="w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 rounded-lg bg-gray-50 overflow-hidden border border-gray-100">
                        {/* Utilise image_url résolu côté serveur (image correspondant aux options de la variante) */}
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={item.product_name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg></div>';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <CubeIcon className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Informations du produit */}
                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <p className="font-medium text-gray-900 text-sm sm:text-base line-clamp-2">
                          {truncateWords(item.product_name, 20)}
                        </p>

                        {/* Options de la variante (ex: Couleur: Noir, Taille: S) */}
                        {item.product_variant?.options && Object.keys(item.product_variant.options).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {Object.entries(item.product_variant.options).map(([key, value]) => (
                              <span
                                key={key}
                                className="text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 px-1.5 py-0.5 rounded"
                              >
                                {key}: {value}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs font-bold bg-gray-100 px-1.5 py-0.5 rounded">x{item.quantity}</span>
                          <span className="text-xs text-gray-500">{Number(item.product_price).toLocaleString('fr-FR')} {deviseCommande(selectedOrder)}</span>
                        </div>
                        {item.notes && (
                          <p className="text-[10px] sm:text-xs text-amber-600 italic mt-1">
                            Note: {item.notes}
                          </p>
                        )}
                      </div>

                      {/* Prix total de l'article */}
                      <div className="text-right flex flex-col justify-center">
                        <p className="text-sm font-bold text-gray-900">
                          {(Number(item.product_price) * item.quantity).toLocaleString('fr-FR')} {deviseCommande(selectedOrder)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="bg-blue-600 rounded-xl p-4 sm:p-5 text-white shadow-md">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium opacity-90">Total à encaisser</span>
                  <span className="text-xl sm:text-2xl font-black">
                    {Number(selectedOrder.total_amount).toLocaleString('fr-FR')} {deviseCommande(selectedOrder)}
                  </span>
                </div>
                {selectedOrder.shipping_fee > 0 && (
                  <div className="pt-2 border-t border-blue-500 border-opacity-30 text-xs flex justify-between opacity-80 italic">
                    <span>Dont frais de livraison:</span>
                    <span>{Number(selectedOrder.shipping_fee).toLocaleString('fr-FR')} {deviseCommande(selectedOrder)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Message de succès */}
            {actionSuccess && (
              <div className="mx-4 sm:mx-6 mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 animate-pulse">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
                <p className="text-green-800 font-bold text-sm">{actionSuccess}</p>
              </div>
            )}

            {/* Pied du modal - Fixe en bas */}
            <div className="border-t border-gray-200 px-4 sm:px-6 py-4 bg-gray-50 flex-shrink-0">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  {/* Bouton "Marquer Livrée" - masqué si déjà marqué comme livré */}
                  {!isMarkedAsDelivered(selectedOrder) && (
                    <button
                      onClick={handleMarkAsDelivered}
                      disabled={actionLoading}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md w-full sm:w-auto order-1 sm:order-none"
                    >
                      {actionLoading ? (
                        <ArrowPathIcon className="h-5 w-5 animate-spin" />
                      ) : (
                        <CheckCircleIcon className="h-5 w-5" />
                      )}
                      Marquer Livrée
                    </button>
                  )}
                  <button
                    onClick={handleOpenProblemModal}
                    disabled={actionLoading}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md w-full sm:w-auto order-2 sm:order-none"
                  >
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    Signaler un Problème
                  </button>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="px-6 py-3 bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 font-bold rounded-xl transition w-full sm:w-auto order-3 sm:order-none"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de signalement de problème - Optimisé pour mobile */}
      {showProblemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* En-tête du modal */}
            <div className="bg-amber-50 border-b border-amber-100 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-amber-900 flex items-center gap-2">
                <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                Signaler un problème
              </h2>
              <button
                onClick={() => setShowProblemModal(false)}
                className="p-1.5 hover:bg-amber-100 rounded-full transition"
              >
                <XMarkIcon className="h-6 w-6 text-amber-700" />
              </button>
            </div>

            {/* Contenu du modal */}
            <div className="p-6">
              <div className="bg-amber-50 rounded-lg p-3 mb-4 flex items-center gap-2 text-sm text-amber-800">
                <CubeIcon className="h-4 w-4" />
                <span>Commande <span className="font-black">#{selectedOrder?.order_number}</span></span>
              </div>
              
              <label htmlFor="problemNote" className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                Décrivez le problème rencontré :
              </label>
              <textarea
                id="problemNote"
                value={problemNote}
                onChange={(e) => setProblemNote(e.target.value)}
                placeholder="Ex: Client absent, adresse introuvable, refus de la commande..."
                rows="5"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none shadow-inner text-sm"
                disabled={actionLoading}
              />
              <p className="text-xs text-gray-500 mt-3 italic flex items-start gap-1">
                <ExclamationCircleIcon className="h-3 w-3 mt-0.5 flex-shrink-0" />
                Cette information sera ajoutée aux notes de la commande.
              </p>
            </div>

            {/* Pied du modal */}
            <div className="border-t border-gray-100 px-6 py-4 flex flex-col sm:flex-row justify-end gap-3 bg-gray-50">
              <button
                onClick={() => setShowProblemModal(false)}
                disabled={actionLoading}
                className="px-6 py-3 bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 font-bold rounded-xl transition disabled:opacity-50 w-full sm:w-auto order-2 sm:order-1"
              >
                Annuler
              </button>
              <button
                onClick={handleReportProblem}
                disabled={actionLoading || !problemNote.trim()}
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md w-full sm:w-auto order-1 sm:order-2"
              >
                {actionLoading ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    Envoyer le rapport
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryPersonPublicView;
